import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase/config';
import { ZoneGeographiqueService } from './ZoneGeographiqueService';
import { AnalyticsBatchService } from './AnalyticsBatchService';
import {
  AnalyticsConfig,
  SessionContext,
  DebutSessionEvent,
  ApparitionEvenementEvent,
  ClicEvenementEvent,
  ConsultationEvenementEvent,
  LikeEvenementEvent,
  DismissEvenementEvent,
  RechercheEvenementEvent,
  FiltreEvenementEvent,
  SourceUtilisateur,
  TypeUtilisateur,
  StatutConnexion,
  PermissionGeolocalisation,
  LocalisationUtilisateur,
  ModeLancement,
  PWAInstalledEvent,
  SEUILS_ENGAGEMENT,
  // Types Phase 2 - Organisateur
  CreationEvenementDebutEvent,
  FormulaireBasicInfoVersDetailsEvent,
  FormulaireDetailsVersPreviewEvent,
  ChampFormulaireModifieEvent,
  CreationEvenementAbandonneeEvent,
  EvenementRecurrentConfigureEvent,
  EvenementSauvegardeDraftEvent,
  EvenementPublieEvent,
  NavigationDashboardOrganisateurEvent,
  // Tentative de publication échouée
  TentativePublicationEchoueeEvent,
  // Types Phase 4 - Profil Organisateur
  ProfilCompletionScoreEvent,
  ProfilPublicConsulteEvent,
  ContactProfilClicEvent,
  ImageProfilUploadeeEvent
} from '../types/analytics.types';

/**
 * Service centralisé pour toutes les opérations analytics
 * Respecte la configuration RGPD et suit la stratégie documentée
 */
export class AnalyticsService {
  private static config: AnalyticsConfig = {
    enabled: true,
    rgpdCompliant: true,
    anonymizeIp: true,
    allowGoogleSignals: false,
    allowPersonalizationSignals: false,
    storage: 'disabled'
  };

  private static sessionContext: SessionContext | null = null;
  
  // ✅ NOUVEAU : Tracker les apparitions par session pour éviter les doublons
  private static trackedAppearances = new Set<string>();

  /**
   * Initialiser le service analytics
   */
  static initialize(): void {
    if (!this.config.enabled) return;

    // Créer un contexte de session
    this.sessionContext = {
      session_id: this.generateSessionId(),
      source_utilisateur: this.detectUserSource(),
      type_utilisateur: 'touriste', // Par défaut, sera mis à jour
      statut_connexion: 'anonyme', // Par défaut, sera mis à jour
      permission_geolocalisation: 'refusee', // Par défaut, sera mis à jour
      zones_visitees: new Set(),
      methode_decouverte_courante: 'navigation_libre',
      heure_debut: Date.now(),
      mode_lancement: this.detectModeLancement() // PWA ou navigateur
    };

    console.log(`📱 Analytics: Mode de lancement détecté: ${this.sessionContext.mode_lancement}`);
    // Logger.listener();
  }

  /**
   * Mettre à jour le contexte utilisateur
   */
  static updateUserContext(
    typeUtilisateur: TypeUtilisateur,
    statutConnexion: StatutConnexion
  ): void {
    if (!this.sessionContext) return;

    this.sessionContext.type_utilisateur = typeUtilisateur;
    this.sessionContext.statut_connexion = statutConnexion;
  }

  /**
   * Mettre à jour la permission de géolocalisation
   */
  static updateGeolocationPermission(permission: PermissionGeolocalisation): void {
    if (!this.sessionContext) return;
    this.sessionContext.permission_geolocalisation = permission;
  }

  /**
   * Déclencher le tracking de début de session
   */
  static trackDebutSession(): void {
    if (!this.sessionContext) return;

    const event: DebutSessionEvent = {
      source_utilisateur: this.sessionContext.source_utilisateur,
      type_utilisateur: this.sessionContext.type_utilisateur,
      statut_connexion: this.sessionContext.statut_connexion,
      permission_geolocalisation: this.sessionContext.permission_geolocalisation,
      localisation_initiale: this.sessionContext.localisation_initiale,
      // localisation_utilisateur pour que le batch converter utilise generateGeohash (precision 5)
      // au lieu de zone_geographique_id (precision 6)
      localisation_utilisateur: this.sessionContext.localisation_initiale
        ? { lat: this.sessionContext.localisation_initiale.lat, lng: this.sessionContext.localisation_initiale.lng }
        : undefined,
      zone_geographique_id: this.sessionContext.localisation_initiale
        ? ZoneGeographiqueService.getZoneId(
            this.sessionContext.localisation_initiale.lat,
            this.sessionContext.localisation_initiale.lng
          )
        : undefined,
      mode_lancement: this.sessionContext.mode_lancement
    };

    this.logAnalyticsEvent('debut_session', event);
  }

  /**
   * Initialiser la session avec géolocalisation optionnelle (Session Resilience Pattern)
   */
  static initializeSession(userLocation?: { latitude: number; longitude: number } | null): void {
    if (!this.sessionContext) {
      this.initialize();
    }

    // ✅ RESILIENCE PATTERN : Session fonctionne avec ou sans géolocalisation
    if (userLocation && this.sessionContext) {
      const localisationUtilisateur = ZoneGeographiqueService.creerLocalisationUtilisateur(userLocation);
      if (localisationUtilisateur) {
        this.sessionContext.localisation_initiale = localisationUtilisateur;
        this.updateGeolocationPermission('accordee');
        
        // Ajouter la zone à la liste des zones visitées
        const zoneId = ZoneGeographiqueService.getZoneId(userLocation.latitude, userLocation.longitude);
        this.sessionContext.zones_visitees.add(zoneId);
      }
    } else if (this.sessionContext) {
      // ✅ NOUVEAU : Session sans géolocalisation (mode dégradé)
      console.warn('🚨 Analytics: Session sans géolocalisation - mode dégradé');
      this.updateGeolocationPermission('refusee');
    }

    this.trackDebutSession();
  }

  /**
   * ✅ NOUVEAU : Enrichir une session existante avec géolocalisation
   * Utilisée quand la géolocalisation arrive après l'initialisation de session
   */
  static enrichSessionWithLocation(userLocation: { latitude: number; longitude: number }): void {
    if (!this.sessionContext) {
      console.warn('🚨 Analytics: Tentative enrichissement session sans session existante');
      return;
    }

    // Ne pas écraser si déjà définie (éviter les re-renders)
    if (this.sessionContext.localisation_initiale) {
      console.log('📍 Analytics: Session déjà enrichie avec localisation, ignoré');
      return;
    }

    const localisationUtilisateur = ZoneGeographiqueService.creerLocalisationUtilisateur(userLocation);
    if (localisationUtilisateur) {
      this.sessionContext.localisation_initiale = localisationUtilisateur;
      this.updateGeolocationPermission('accordee');
      
      // Ajouter la zone à la liste des zones visitées
      const zoneId = ZoneGeographiqueService.getZoneId(userLocation.latitude, userLocation.longitude);
      this.sessionContext.zones_visitees.add(zoneId);
      
      console.log('✅ Analytics: Session enrichie avec géolocalisation');
    }
  }

  /**
   * Mettre à jour la localisation utilisateur
   */
  static setUserLocation(location: { latitude: number; longitude: number } | undefined): void {
    if (!this.sessionContext || !location) return;

    const localisationUtilisateur = ZoneGeographiqueService.creerLocalisationUtilisateur(location);
    if (localisationUtilisateur) {
      const wasEmpty = !this.sessionContext.localisation_initiale;
      this.sessionContext.localisation_initiale = localisationUtilisateur;

      // Ajouter la zone à la liste des zones visitées
      const zoneId = ZoneGeographiqueService.getZoneId(location.latitude, location.longitude);
      this.sessionContext.zones_visitees.add(zoneId);

      if (wasEmpty) {
        console.log(`✅ Analytics: Session enrichie avec géolocalisation (zone: ${zoneId.substring(0, 5)}*)`);
      }
    }
  }

  /**
   * ✅ Tracker une connexion de zone (recherche ou changement de position)
   * Chaque connexion est comptée pour refléter le trafic réel
   */
  static trackConnexionZone(
    location: { latitude: number; longitude: number },
    source: 'geolocation' | 'search' | 'map_move' | 'cache' | 'fallback'
  ): void {
    if (!location) return;

    // ✅ FILTRE : Ne tracker que les touristes (pas les organisateurs)
    const userType = this.sessionContext?.type_utilisateur || 'touriste';
    if (userType === 'organisateur') {
      console.log(`📍 Analytics: Connexion zone ignorée (utilisateur = organisateur)`);
      return;
    }

    // Générer le geohash et le préfixe de zone
    const geohash = ZoneGeographiqueService.getZoneId(location.latitude, location.longitude);
    const zonePrefix = geohash.substring(0, 5); // Précision ~5km

    // Créer l'event avec les informations de localisation
    const event = {
      localisation_utilisateur: {
        lat: location.latitude,
        lng: location.longitude
      },
      zone_geographique_id: geohash,
      source: source,
      type_utilisateur: this.sessionContext?.type_utilisateur || 'touriste'
    };

    console.log(`📍 Analytics: Connexion zone trackée - ${zonePrefix} (source: ${source}) [1ère du jour]`);
    this.logAnalyticsEvent('connexion_zone', event);
  }

  // ===== TRACKING DES ÉVÉNEMENTS =====

  /**
   * Tracker l'apparition d'un événement (une seule fois par session)
   * ✅ RESILIENCE PATTERN : Fonctionne avec ou sans géolocalisation
   */
  static trackApparitionEvenement(
    evenement: any,
    vueSource: 'card' | 'carte_pin' | 'carte_preview',
    userLocation: LocalisationUtilisateur | null,
    durationMs?: number
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    // ✅ ANTI-DOUBLON : Vérifier si déjà tracké dans cette session
    const appearanceKey = `${evenement.id}_${vueSource}`;
    if (this.trackedAppearances.has(appearanceKey)) {
      console.log(`📊 Analytics: Apparition déjà trackée pour ${evenement.id} (${vueSource})`);
      return;
    }

    // ✅ Marquer comme tracké
    this.trackedAppearances.add(appearanceKey);

    const event: ApparitionEvenementEvent = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      vue_source: vueSource,
      methode_decouverte: this.sessionContext.methode_decouverte_courante,
      localisation_utilisateur: userLocation,
      zone_geographique_id: userLocation
        ? ZoneGeographiqueService.getZoneId(userLocation.lat, userLocation.lng)
        : null,
      duration_ms: durationMs
    };

    console.log(`✅ Analytics: Apparition trackée pour ${evenement.id} (${vueSource})${durationMs ? ` — ${durationMs}ms` : ''}`);
    this.logAnalyticsEvent('apparition_evenement', event);
  }

  /**
   * Tracker un clic sur événement
   */
  static trackClicEvenement(
    evenement: any,
    vueSource: 'card' | 'carte_pin' | 'carte_preview',
    userLocation: LocalisationUtilisateur | null,
    tempsdepuisapparition: number = 0
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    // ✅ RESILIENCE PATTERN : Calculer metrics avec ou sans géolocalisation
    const event: ClicEvenementEvent = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      vue_source: vueSource,
      methode_decouverte: this.sessionContext.methode_decouverte_courante,
      localisation_utilisateur: userLocation || undefined,
      zone_geographique_id: userLocation ? 
        ZoneGeographiqueService.getZoneId(userLocation.lat, userLocation.lng) : 
        'unknown',
      temps_depuis_apparition: tempsdepuisapparition
    };

    this.logAnalyticsEvent('clic_evenement', event);
  }

  /**
   * Tracker la consultation d'un événement
   */
  static trackConsultationEvenement(
    evenement: any,
    dureeConsultation: number,
    detailsConsultation: {
      hasScrolled: boolean;
      sectionsViewed: string[];
      actionsPerformed: string[];
      scrollDepth: number;
    },
    userLocation?: LocalisationUtilisateur | null
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    // ✅ RESILIENCE PATTERN : Tracking fonctionne avec ou sans géolocalisation
    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;

    // Déterminer le type de consultation
    let typeConsultation: 'rapide' | 'valide' | 'approfondie';
    if (dureeConsultation < 3000) {
      typeConsultation = 'rapide';
    } else if (dureeConsultation < SEUILS_ENGAGEMENT.consultation_approfondie) {
      typeConsultation = 'valide';
    } else {
      typeConsultation = 'approfondie';
    }

    // ✅ RESILIENCE PATTERN : Calculer zones avec ou sans géolocalisation
    const userZoneId = localisationUtilisateur ? 
      ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng) : 
      'unknown';
    const eventZoneId = evenement.latitude && evenement.longitude 
      ? ZoneGeographiqueService.getZoneId(evenement.latitude, evenement.longitude)
      : userZoneId;

    const event: ConsultationEvenementEvent = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      duree_consultation: dureeConsultation,
      type_consultation: typeConsultation,
      est_consultation_valide: dureeConsultation >= SEUILS_ENGAGEMENT.consultation_valide,
      a_scrolle: detailsConsultation.hasScrolled,
      sections_vues: detailsConsultation.sectionsViewed,
      actions_effectuees: detailsConsultation.actionsPerformed,
      profondeur_scroll: detailsConsultation.scrollDepth,
      zone_geographique_id: userZoneId,
      zone_evenement_id: eventZoneId,
      meme_zone: userZoneId === eventZoneId
    };

    this.logAnalyticsEvent('consultation_evenement', event);
  }

  /**
   * Tracker un like d'événement
   */
  static trackLikeEvenement(
    evenement: any,
    methode: 'swipe_droite' | 'bouton_favori',
    contexte: 'card' | 'carte' | 'modale' | 'carte_preview',
    userLocation?: LocalisationUtilisateur | null
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    // ✅ RESILIENCE PATTERN : Tracking fonctionne avec ou sans géolocalisation
    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;

    const event: LikeEvenementEvent = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      methode,
      contexte,
      localisation_utilisateur: localisationUtilisateur,
      zone_geographique_id: localisationUtilisateur ? 
        ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng) : 
        'unknown'
    };

    this.logAnalyticsEvent('like_evenement', event);
  }

  /**
   * Tracker un dismiss d'événement
   */
  static trackDismissEvenement(
    evenement: any,
    contexte: 'card' | 'carte' | 'modale' | 'carte_preview' = 'carte',
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: DismissEvenementEvent = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      methode: 'swipe_gauche',
      contexte: contexte,
      localisation_utilisateur: localisationUtilisateur,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('dismiss_evenement', event);
  }

  /**
   * Tracker une navigation vers une application externe (directions, etc.)
   */
  static trackNavigation(
    evenement: any,
    typeNavigation: 'directions' | 'website' | 'phone' | 'email' | 'calendar',
    contexte: 'card' | 'carte' | 'modale' | 'carte_preview',
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      type_navigation: typeNavigation,
      contexte: contexte,
      localisation_utilisateur: localisationUtilisateur,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('navigation_externe', event);
  }

  /**
   * Tracker une action de swipe (action physique distincte du like logique)
   */
  static trackSwipeAction(
    evenement: any,
    typeSwipe: 'swipe_droite' | 'swipe_gauche',
    contexte: 'card' | 'carte' | 'modale' | 'carte_preview',
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext || !evenement.id) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event = {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      type_swipe: typeSwipe,
      contexte: contexte,
      localisation_utilisateur: localisationUtilisateur,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('swipe_action', event);
  }

  /**
   * Marquer qu'une recherche a été effectuée
   */
  static markSearchPerformed(): void {
    if (!this.sessionContext) return;
    this.sessionContext.methode_decouverte_courante = 'apres_recherche';
  }

  /**
   * Marquer qu'un filtre a été appliqué
   */
  static markFilterApplied(): void {
    if (!this.sessionContext) return;
    
    // Logique pour déterminer la méthode de découverte
    if (this.sessionContext.methode_decouverte_courante === 'apres_recherche') {
      this.sessionContext.methode_decouverte_courante = 'apres_recherche_et_filtres';
    } else {
      this.sessionContext.methode_decouverte_courante = 'apres_filtres';
    }
  }

  /**
   * Tracker une recherche d'événements
   */
  static trackRechercheEvenement(
    termesRecherche: string,
    nombreResultats: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    this.sessionContext.derniere_recherche = termesRecherche;
    
    const event: RechercheEvenementEvent = {
      termes_recherche: termesRecherche,
      nombre_resultats: nombreResultats,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng),
      filtres_actifs: this.sessionContext.derniers_filtres || []
    };

    this.logAnalyticsEvent('recherche_evenement', event);
  }

  /**
   * Tracker l'application de filtres
   */
  static trackFiltreEvenement(
    filtresAppliques: any,
    nombreResultats: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: FiltreEvenementEvent = {
      filtres_appliques: filtresAppliques,
      nombre_resultats: nombreResultats,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('filtre_evenement', event);
  }

  /**
   * Tracker un clic sur un pin de carte (action utilisateur physique)
   */
  static trackPinClick(
    pinLocation: { lat: number; lng: number },
    eventsInPin: any[],
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext || eventsInPin.length === 0) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event = {
      zone_geographique_id: ZoneGeographiqueService.getZoneId(pinLocation.lat, pinLocation.lng),
      nb_events_dans_pin: eventsInPin.length,
      events_ids: eventsInPin.map(e => e.id).filter(Boolean),
      organisateurs_ids: Array.from(new Set(eventsInPin.map(e => e.organizerId).filter(Boolean))),
      localisation_pin: {
        lat: pinLocation.lat,
        lng: pinLocation.lng
      },
      localisation_utilisateur: localisationUtilisateur,
      zone_geographique_utilisateur: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('clic_pin_carte', event);
  }

  // ===== TRACKING UX : Vue, Partage, Affordance, Navigation =====

  /**
   * Tracker un changement de mode de vue (liste ↔ carte)
   */
  static trackViewModeChange(
    newMode: 'list' | 'map',
    previousMode: 'list' | 'map'
  ): void {
    if (!this.sessionContext) return;

    const event = {
      nouveau_mode: newMode,
      ancien_mode: previousMode,
      plateforme: this.detectPlateforme(),
      methode_decouverte: this.sessionContext.methode_decouverte_courante
    };

    this.logAnalyticsEvent('changement_vue', event);
  }

  /**
   * Tracker l'affichage de l'affordance de swipe (animation wiggle)
   */
  static trackSwipeAffordanceShown(
    context: 'card' | 'modal'
  ): void {
    if (!this.sessionContext) return;

    this.logAnalyticsEvent('swipe_affordance_affichee', {
      contexte: context,
      plateforme: this.detectPlateforme()
    });
  }

  /**
   * Tracker si l'utilisateur a swipé après avoir vu l'affordance
   */
  static trackSwipeAfterAffordance(
    context: 'card' | 'modal',
    didSwipe: boolean
  ): void {
    if (!this.sessionContext) return;

    this.logAnalyticsEvent('swipe_apres_affordance', {
      contexte: context,
      a_swipe: didSwipe
    });
  }

  /**
   * Tracker un clic sur la bottom nav ou menu desktop
   */
  static trackBottomNavClick(
    item: string,
    action: 'open' | 'close' | 'navigate' | 'toggle'
  ): void {
    if (!this.sessionContext) return;

    this.logAnalyticsEvent('navigation_bottom_bar', {
      element: item,
      action: action,
      plateforme: this.detectPlateforme()
    });
  }

  /**
   * Tracker un partage d'événement
   */
  static trackShareEvent(
    evenement: any,
    method: 'native' | 'whatsapp' | 'clipboard',
    contexte: string
  ): void {
    if (!this.sessionContext || !evenement?.id) return;

    const localisationUtilisateur = this.sessionContext.localisation_initiale;

    this.logAnalyticsEvent('partage_evenement', {
      id_evenement: evenement.id,
      id_organisateur: evenement.organizerId || undefined,
      methode_partage: method,
      contexte: contexte,
      zone_geographique_id: localisationUtilisateur
        ? ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
        : 'unknown'
    });
  }

  // ===== TRACKING LOCATION FALLBACK TOAST =====

  /**
   * Tracker l'affichage du toast de fallback location
   */
  static trackLocationFallbackToastShown(): void {
    this.logAnalyticsEvent('location_fallback_toast_affiche', {
      plateforme: this.detectPlateforme()
    });
  }

  /**
   * Tracker l'interaction avec le toast de fallback location
   */
  static trackLocationFallbackToastAction(
    action: 'locate_me' | 'dismiss' | 'auto_dismiss',
    result?: 'geolocation_success' | 'geolocation_denied'
  ): void {
    this.logAnalyticsEvent('location_fallback_toast_action', {
      action,
      resultat: result || undefined,
      plateforme: this.detectPlateforme()
    });
  }

  // ===== TRACKING PWA =====

  /**
   * Tracker l'installation de la PWA (événement unique par appareil)
   * Appelé uniquement quand l'événement 'appinstalled' se déclenche
   */
  static trackPWAInstalled(methodeInstallation: 'prompt_natif' | 'menu_navigateur' = 'prompt_natif'): void {
    const localisationUtilisateur = this.sessionContext?.localisation_initiale;

    const event: PWAInstalledEvent = {
      methode_installation: methodeInstallation,
      plateforme: this.detectPlateforme(),
      navigateur: this.detectNavigateur(),
      zone_geographique_id: localisationUtilisateur
        ? ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
        : undefined
    };

    console.log('📲 Analytics: Installation PWA trackée !', event);
    this.logAnalyticsEvent('pwa_installed', event);
  }

  /**
   * Obtenir le mode de lancement actuel de la session
   */
  static getModeLancement(): ModeLancement {
    return this.sessionContext?.mode_lancement || this.detectModeLancement();
  }

  // ===== MÉTHODES UTILITAIRES =====

  /**
   * Générer un identifiant de session unique
   */
  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Détecter la source de l'utilisateur
   */
  private static detectUserSource(): SourceUtilisateur {
    const referrer = document.referrer.toLowerCase();
    const currentUrl = window.location.href.toLowerCase();

    // Vérifier si vient de W2D.TODAY
    if (referrer.includes('w2d.today') || currentUrl.includes('utm_source=w2d')) {
      return 'w2d_today';
    }

    // Vérifier si vient de Google
    if (referrer.includes('google.') || currentUrl.includes('utm_source=google')) {
      return 'recherche_google';
    }

    // Vérifier si vient des réseaux sociaux
    const socialNetworks = ['facebook.', 'instagram.', 'twitter.', 'linkedin.', 'tiktok.'];
    if (socialNetworks.some(network => referrer.includes(network)) ||
        currentUrl.includes('utm_source=social')) {
      return 'reseaux_sociaux';
    }

    return 'url_directe';
  }

  /**
   * Détecter le mode de lancement (PWA installée ou navigateur)
   */
  static detectModeLancement(): ModeLancement {
    // Vérifier si l'app est lancée en mode standalone (PWA installée)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return 'pwa';
    }

    // iOS Safari : vérifier navigator.standalone
    if ((navigator as any).standalone === true) {
      return 'pwa';
    }

    // Vérifier aussi display-mode: fullscreen (certaines PWA)
    if (window.matchMedia('(display-mode: fullscreen)').matches) {
      return 'pwa';
    }

    return 'navigateur';
  }

  /**
   * Détecter la plateforme de l'utilisateur
   */
  private static detectPlateforme(): 'ios' | 'android' | 'desktop' | 'unknown' {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/ipad|iphone|ipod/.test(userAgent)) {
      return 'ios';
    }
    if (/android/.test(userAgent)) {
      return 'android';
    }
    if (/windows|macintosh|linux/.test(userAgent) && !/mobile/.test(userAgent)) {
      return 'desktop';
    }

    return 'unknown';
  }

  /**
   * Détecter le navigateur de l'utilisateur
   */
  private static detectNavigateur(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';

    return 'Unknown';
  }

  /**
   * Logger un événement analytics via le système de batch
   */
  private static logAnalyticsEvent(eventName: string, eventParams: any): void {
    
    if (!this.config.enabled) {
      return;
    }

    try {
      // Utiliser le nouveau système de batch unifié
      const batchService = AnalyticsBatchService.getInstance();
      batchService.addEvent(eventName, eventParams);
      
    } catch (error) {
      console.error('❌ Analytics logging error:', error);
      
      // Fallback vers l'ancien système en cas d'erreur
      try {
        const cleanParams = this.cleanFirebaseParams(eventParams);
        logEvent(analytics, eventName, cleanParams);
      } catch (fallbackError) {
        console.error('❌ Fallback analytics error:', fallbackError);
      }
    }
  }

  /**
   * Nettoyer les paramètres pour Firebase Analytics
   */
  private static cleanFirebaseParams(params: any): any {
    const cleanParams: any = {};
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      
      // Firebase n'accepte que certains types
      if (value !== null && value !== undefined) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          cleanParams[key] = value;
        } else if (Array.isArray(value)) {
          cleanParams[key] = value.join(',');
        } else if (typeof value === 'object') {
          cleanParams[key] = JSON.stringify(value);
        }
      }
    });
    
    return cleanParams;
  }

  /**
   * Obtenir le contexte de session actuel
   */
  static getSessionContext(): SessionContext | null {
    return this.sessionContext;
  }

  // ===== MÉTHODES PHASE 2 : ANALYTICS ORGANISATEUR =====

  /**
   * Tracker le début de création d'un événement
   */
  static trackCreationEvenementDebut(
    organisateurId: string,
    typeEvenement: 'unique' | 'recurrent',
    estPremierEvenement: boolean,
    aDraftsExistants: boolean,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) {
      return;
    }

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: CreationEvenementDebutEvent = {
      id_organisateur: organisateurId,
      type_evenement: typeEvenement,
      est_premier_evenement: estPremierEvenement,
      a_drafts_existants: aDraftsExistants,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('creation_evenement_debut', event);
  }

  /**
   * Tracker le passage de basic_info vers details
   */
  static trackFormulaireBasicInfoVersDetails(
    organisateurId: string,
    tempsPasseEtape: number,
    champsRemplis: string[],
    erreursRencontrees: string[],
    completudePourcentage: number,
    typeEvenement?: 'unique' | 'recurrent',
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: FormulaireBasicInfoVersDetailsEvent = {
      id_organisateur: organisateurId,
      temps_passe_etape: tempsPasseEtape,
      champs_remplis: champsRemplis,
      erreurs_rencontrees: erreursRencontrees,
      completude_pourcentage: completudePourcentage,
      type_evenement: typeEvenement,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('formulaire_basic_info_vers_details', event);
  }

  /**
   * Tracker le passage de details vers preview
   */
  static trackFormulaireDetailsVersPreview(
    organisateurId: string,
    tempsPasseEtape: number,
    champsRemplis: string[],
    erreursRencontrees: string[],
    completudePourcentage: number,
    typeEvenement?: 'unique' | 'recurrent',
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: FormulaireDetailsVersPreviewEvent = {
      id_organisateur: organisateurId,
      temps_passe_etape: tempsPasseEtape,
      champs_remplis: champsRemplis,
      erreurs_rencontrees: erreursRencontrees,
      completude_pourcentage: completudePourcentage,
      type_evenement: typeEvenement,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('formulaire_details_vers_preview', event);
  }

  /**
   * Tracker la modification d'un champ spécifique
   */
  static trackChampFormulaireModifie(
    organisateurId: string,
    etape: 'basic_info' | 'details' | 'preview',
    champ: string,
    nombreModifications: number,
    valeurLongueur?: number,
    tentativesGeolocalisation?: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: ChampFormulaireModifieEvent = {
      id_organisateur: organisateurId,
      etape,
      champ,
      nombre_modifications: nombreModifications,
      valeur_longueur: valeurLongueur,
      tentatives_geolocalisation: tentativesGeolocalisation,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('champ_formulaire_modifie', event);
  }

  /**
   * Tracker l'abandon du processus de création
   */
  static trackCreationEvenementAbandonnee(
    organisateurId: string,
    etapeAbandon: 'basic_info' | 'details' | 'preview',
    dernierChampModifie: string,
    tempsTotalSession: number,
    completudeFinale: number,
    raisonProbable: 'erreur_validation' | 'fermeture_navigateur' | 'inactivite',
    champsRemplis: string[],
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: CreationEvenementAbandonneeEvent = {
      id_organisateur: organisateurId,
      etape_abandon: etapeAbandon,
      dernier_champ_modifie: dernierChampModifie,
      temps_total_session: tempsTotalSession,
      completude_finale: completudeFinale,
      raison_probable: raisonProbable,
      champs_remplis: champsRemplis,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('creation_evenement_abandonnee', event);
  }

  /**
   * Tracker la configuration d'un événement récurrent
   */
  static trackEvenementRecurrentConfigure(
    organisateurId: string,
    frequence: 'daily' | 'weekly',
    typeFin: 'date' | 'occurrences',
    dureeSerie: number,
    nombreJoursSemaine?: number,
    nombreOccurrences?: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: EvenementRecurrentConfigureEvent = {
      id_organisateur: organisateurId,
      frequence,
      nombre_jours_semaine: nombreJoursSemaine,
      type_fin: typeFin,
      duree_serie: dureeSerie,
      nombre_occurrences: nombreOccurrences,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('evenement_recurrent_configure', event);
  }

  /**
   * Tracker la sauvegarde en draft
   */
  static trackEvenementSauvegardeDraft(
    organisateurId: string,
    draftId: string,
    etapeSauvegarde: 'basic_info' | 'details' | 'preview',
    completudePourcentage: number,
    champsRemplis: string[],
    estPremierDraft: boolean,
    tempsAvantSauvegarde: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: EvenementSauvegardeDraftEvent = {
      id_organisateur: organisateurId,
      id_draft: draftId,
      etape_sauvegarde: etapeSauvegarde,
      completude_pourcentage: completudePourcentage,
      champs_remplis: champsRemplis,
      est_premier_draft: estPremierDraft,
      temps_avant_sauvegarde: tempsAvantSauvegarde,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('evenement_sauvegarde_draft', event);
  }

  /**
   * Tracker la publication finale d'un événement
   */
  static trackEvenementPublie(
    organisateurId: string,
    evenementId: string,
    tempsTotalCreation: number,
    nombreSessionsCreation: number,
    etaitDraft: boolean,
    completudeFinale: number,
    typeEvenement: 'unique' | 'recurrent',
    nombreOccurrences?: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: EvenementPublieEvent = {
      id_organisateur: organisateurId,
      id_evenement: evenementId,
      temps_total_creation: tempsTotalCreation,
      nombre_sessions_creation: nombreSessionsCreation,
      etait_draft: etaitDraft,
      completude_finale: completudeFinale,
      type_evenement: typeEvenement,
      nombre_occurrences: nombreOccurrences,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('evenement_publie', event);
  }

  /**
   * Tracker la navigation dans le dashboard organisateur
   */
  static trackNavigationDashboardOrganisateur(
    organisateurId: string,
    pageVisitee: 'events_actifs' | 'drafts' | 'events_passes' | 'profil' | 'statistiques' | 'creation',
    tempsPasse: number,
    actionsEffectuees: string[],
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: NavigationDashboardOrganisateurEvent = {
      id_organisateur: organisateurId,
      page_visitee: pageVisitee,
      temps_passe: tempsPasse,
      actions_effectuees: actionsEffectuees,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('navigation_dashboard_organisateur', event);
  }

  /**
   * Tracker la connexion d'un organisateur à son dashboard
   * (métrique "Connexions Orgas" du dashboard admin - audit 2026-06-11, finding 6)
   * Pas de dépendance à sessionContext ni à la géolocalisation :
   * l'event doit partir même sans géoloc (geohash 'unknown' accepté côté batch).
   * L'anti-doublon par session navigateur est géré par l'appelant (sessionStorage).
   */
  static trackOrganizerLogin(organisateurId: string): void {
    if (!organisateurId) return;

    this.logAnalyticsEvent('organizer_login', {
      id_organisateur: organisateurId,
      vue_source: 'dashboard_organisateur'
    });
  }

  /**
   * Tracker une tentative de publication échouée
   */
  static trackTentativePublicationEchouee(
    organisateurId: string,
    etapeActuelle: 'basic_info' | 'details' | 'preview',
    champsMananquants: string[],
    completudeActuelle: number,
    tempsAvantTentative: number,
    nombreTentatives: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) {
      return;
    }

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) {
      return;
    }

    const event: TentativePublicationEchoueeEvent = {
      id_organisateur: organisateurId,
      etape_actuelle: etapeActuelle,
      champs_manquants: champsMananquants,
      completude_actuelle: completudeActuelle,
      temps_avant_tentative: tempsAvantTentative,
      nombre_tentatives: nombreTentatives,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('tentative_publication_echouee', event);
  }

  // ===== MÉTHODES PHASE 4 : ANALYTICS PROFIL ORGANISATEUR =====

  /**
   * Tracker le score de complétion du profil
   */
  static trackProfilCompletionScore(
    organisateurId: string,
    scoreCompletion: number,
    elementsCompletes: string[],
    elementsManquants: string[],
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: ProfilCompletionScoreEvent = {
      id_organisateur: organisateurId,
      score_completion: scoreCompletion,
      elements_completes: elementsCompletes,
      elements_manquants: elementsManquants,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('profil_completion_score', event);
  }

  /**
   * Tracker la consultation d'un profil public
   */
  static trackProfilPublicConsulte(
    organisateurConsulteId: string,
    typeVisiteur: 'touriste' | 'organisateur' | 'anonyme',
    estOrganisateurVisiteur: boolean,
    elementsProfilVisibles: string[],
    tempsConsultation: number,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: ProfilPublicConsulteEvent = {
      id_organisateur_consulte: organisateurConsulteId,
      type_visiteur: typeVisiteur,
      est_organisateur_visiteur: estOrganisateurVisiteur,
      elements_profil_visibles: elementsProfilVisibles,
      temps_consultation: tempsConsultation,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('profil_public_consulte', event);
  }

  /**
   * Tracker les clics sur éléments de contact
   */
  static trackContactProfilClic(
    organisateurId: string,
    typeContact: 'email' | 'telephone' | 'site_web' | 'instagram',
    depuisVue: 'profil_public' | 'evenement_modal' | 'card_evenement',
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: ContactProfilClicEvent = {
      id_organisateur: organisateurId,
      type_contact: typeContact,
      depuis_vue: depuisVue,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('contact_profil_clic', event);
  }

  /**
   * Tracker l'upload d'images profil
   */
  static trackImageProfilUploadee(
    organisateurId: string,
    typeImage: 'avatar' | 'galerie',
    tailleFichier: number,
    formatImage: string,
    succesUpload: boolean,
    nombreImagesGalerieTotal?: number,
    tentativeNumero: number = 1,
    userLocation?: LocalisationUtilisateur
  ): void {
    if (!this.sessionContext) return;

    const localisationUtilisateur = userLocation || this.sessionContext.localisation_initiale;
    if (!localisationUtilisateur) return;

    const event: ImageProfilUploadeeEvent = {
      id_organisateur: organisateurId,
      type_image: typeImage,
      taille_fichier: tailleFichier,
      format_image: formatImage,
      succes_upload: succesUpload,
      nombre_images_galerie_total: nombreImagesGalerieTotal,
      tentative_numero: tentativeNumero,
      zone_geographique_id: ZoneGeographiqueService.getZoneId(localisationUtilisateur.lat, localisationUtilisateur.lng)
    };

    this.logAnalyticsEvent('image_profil_uploadee', event);
  }

  /**
   * Utilitaire pour calculer le score de complétion d'un profil
   */
  static calculateProfileCompletionScore(profile: any): {
    score: number;
    elementsCompletes: string[];
    elementsManquants: string[];
  } {
    // Helper to check if a string field has real content (not just whitespace or default values)
    const hasContent = (value: any): boolean => {
      if (!value) return false;
      if (typeof value !== 'string') return false;
      const trimmed = value.trim();
      // Exclude default/placeholder values
      if (trimmed === '' || trimmed === 'https://' || trimmed === 'http://') return false;
      return true;
    };

    // Contact info: at least one of phone, website, instagram, or email
    const hasContactInfo = !!(
      hasContent(profile?.phone) ||
      hasContent(profile?.phoneNumber) ||
      (hasContent(profile?.website) && profile.website.length > 10) || // More than just "https://"
      hasContent(profile?.instagram) ||
      hasContent(profile?.email) ||
      hasContent(profile?.organizerContact)
    );

    const elements = {
      organizer: hasContent(profile?.organizer),
      subtitle: hasContent(profile?.subtitle),
      description: hasContent(profile?.description),
      location: hasContent(profile?.location),
      organizerImage: hasContent(profile?.organizerImage),
      galleryImages: !!(profile?.galleryImages && Array.isArray(profile.galleryImages) && profile.galleryImages.length > 0),
      contactInfo: hasContactInfo
    };

    const elementsCompletes = Object.keys(elements).filter(key => elements[key as keyof typeof elements]);
    const elementsManquants = Object.keys(elements).filter(key => !elements[key as keyof typeof elements]);
    const score = Math.round((elementsCompletes.length / Object.keys(elements).length) * 100);

    return {
      score,
      elementsCompletes,
      elementsManquants
    };
  }

  // ===== CONFIGURATION BATCH =====

  /**
   * Configurer l'intervalle de batch (en millisecondes)
   */
  static setBatchInterval(intervalMs: number): void {
    AnalyticsBatchService.getInstance().setBatchInterval(intervalMs);
  }

  /**
   * Forcer l'envoi immédiat du batch
   */
  static forceBatch(): void {
    AnalyticsBatchService.getInstance().forceBatch();
  }

  /**
   * Obtenir les statistiques du batch
   */
  static getBatchStats(): { ga4Pending: number; supabasePending: number; interval: number } {
    return AnalyticsBatchService.getInstance().getStats();
  }

  /**
   * Retourner le status d'initialisation pour debugging (version étendue)
   */
  static getStatus(): any {
    return {
      config: this.config,
      sessionContext: this.sessionContext,
      isInitialized: this.sessionContext !== null,
      batchStats: this.getBatchStats()
    };
  }

  /**
   * Tracker la fin de session avec métriques d'engagement
   */
  static trackFinSession(): void {
    if (!this.sessionContext) return;

    const dureeSession = Math.round((Date.now() - this.sessionContext.heure_debut) / 1000); // en secondes
    const estSessionActive = dureeSession >= SEUILS_ENGAGEMENT.session_active / 1000; // >= 30 secondes

    const event = {
      id_session: this.sessionContext.session_id,
      duree_session: dureeSession,

      // Contexte session
      type_utilisateur: this.sessionContext.type_utilisateur,
      source_session: this.sessionContext.source_utilisateur,
      statut_connexion: this.sessionContext.statut_connexion,
      permission_geolocalisation: this.sessionContext.permission_geolocalisation,
      mode_lancement: this.sessionContext.mode_lancement, // PWA ou navigateur

      // Exploration géographique
      zones_explorees: Array.from(this.sessionContext.zones_visitees),
      type_exploration: this.sessionContext.zones_visitees.size <= 1 ? 'locale' :
                       this.sessionContext.zones_visitees.size <= 3 ? 'regionale' : 'large',

      // État de la session
      est_session_active: estSessionActive,
      methode_decouverte_finale: this.sessionContext.methode_decouverte_courante,

      // Zone géographique finale
      zone_geographique_id: this.sessionContext.localisation_initiale
        ? ZoneGeographiqueService.getZoneId(
            this.sessionContext.localisation_initiale.lat,
            this.sessionContext.localisation_initiale.lng
          )
        : 'unknown'
    };

    this.logAnalyticsEvent('fin_session', event);
    // Logger.analytics();
  }

  /**
   * ✅ NOUVEAU : Réinitialiser le service (pour les tests et nouveau session)
   */
  static reset(): void {
    this.sessionContext = null;
    this.trackedAppearances.clear();
    console.log('🔄 Analytics: Service réinitialisé');
  }
}

export default AnalyticsService;